/**
 * Macro #26 · Voxel LOD gating — PURE (Increment 2).
 *
 * Reuses the FROZEN canonical zoom thresholds from `domain/lod.ts` (OVERVIEW
 * 4.27 / DETAIL 7.0). Macro #26 changes only what is PRESENTED inside Level 2 —
 * NEVER the thresholds. The voxel runtime lives entirely inside Level 2
 * (INTERMEDIATE); it fades toward Level 3 and is gone at Level 3 (DETAIL), where
 * the full Canvas is 100%.
 */
import { DETAIL_MIN_ZOOM, OVERVIEW_MAX_ZOOM, lodBandForZoom } from "./lod";

/** Where the voxel scene starts fading as the grid zooms toward Level 3. */
export const VOXEL_FADE_START_ZOOM = 6.0;

/** The voxel runtime is only ever alive inside Level 2 (INTERMEDIATE band). */
export function voxelBandActive(scale: number): boolean {
  return lodBandForZoom(scale) === "intermediate";
}

/** Voxel scene opacity for the current grid zoom: full across most of Level 2,
 * linearly fading to 0 as the camera approaches the Level 3 threshold; 0 at/above
 * Level 3. */
export function voxelSceneOpacity(scale: number): number {
  if (!Number.isFinite(scale)) return 0;
  if (scale <= OVERVIEW_MAX_ZOOM) return 0; // Level 1 — no voxels
  if (scale >= DETAIL_MIN_ZOOM) return 0; // Level 3 — voxels gone
  if (scale <= VOXEL_FADE_START_ZOOM) return 1;
  // fade band VOXEL_FADE_START_ZOOM..DETAIL_MIN_ZOOM → 1..0
  const t = (scale - VOXEL_FADE_START_ZOOM) / (DETAIL_MIN_ZOOM - VOXEL_FADE_START_ZOOM);
  return Math.max(0, Math.min(1, 1 - t));
}

/** Canvas underlay opacity while the 3D scene is mounted+visible: the Canvas is
 * attenuated (translucent) under a strong voxel scene and returns toward full as
 * the voxel scene fades out toward Level 3. */
export function canvasUnderlayOpacity(scale: number, entered: boolean): number {
  if (!entered) return 1;
  const v = voxelSceneOpacity(scale);
  // Canvas floor 0.35 under a fully-opaque voxel scene → back to 1 as it fades.
  return Math.max(0.35, 1 - v * 0.65);
}

/** Minimal shape of a published snapshot's optional 3D scene. */
export interface PublishedScene3D {
  work_arts?: unknown[];
  voxel_count?: number;
}

/** A territory offers an ENTER-3D affordance only when it has a VALID published
 * 3D scene with at least one voxel. */
export function enter3dEligible(scene: PublishedScene3D | null | undefined): boolean {
  if (!scene) return false;
  const count = typeof scene.voxel_count === "number" ? scene.voxel_count : (scene.work_arts?.length ?? 0);
  return count > 0;
}
